import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Plus, Edit, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

import {
  deleteLocationService,
  deleteDepartmentService,
  deleteDesignationService,
  createLocationService,
  updateLocationService,
  createDepartmentService,
  updateDepartmentService,
  createDesignationService,
  updateDesignationService,
} from "@/services/adminService";

import { State, City } from "country-state-city";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useSubscription } from "@/contexts/SubscriptionContext";

import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import OfficeLocationPicker from "@/components/common/OfficeLocationPicker";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";

// States list
const INDIA_STATES = State.getStatesOfCountry("IN");

// -------------------------------------------------------
// MAIN COMPONENT
// -------------------------------------------------------
const WorkLocationDeptDesignationTab = ({
  locations,
  setLocations,
  departments,
  setDepartments,
  designations,
  setDesignations,
}) => {
  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [type, setType] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const { reloadOnboardingStatus } = useOnboarding();
  const { hasFeature, isPaidOnline } = useSubscription();

  const showDepartmentDesignation = hasFeature("department_mapping");
  const addMultipleLocations = hasFeature("multiple_branches");
  const navigate = useNavigate();

  const openConfirm = (cfg) => {
    setConfirmData(cfg);
    setConfirmOpen(true);
  };

  // FOR LOCATIONS → single object
  const emptyLocation = {
    name: "",
    state: "",
    city: "",
    pincode: "",
    address: "",
    latitude: "",
    longitude: "",
    radius: 100,
  };

  // FOR DEPARTMENT / DESIGNATION → array of row objects
  const [rows, setRows] = useState([{ name: "" }]);

  const [locationForm, setLocationForm] = useState(emptyLocation);
  const [mapOpen, setMapOpen] = useState(false);
  const { can } = usePermission();

  const { canView, canCreate, canEdit, canDelete } = createCanHelpers(can);

  // Work Location Permissions
  const canViewLocation = canView("work_locations_configuration");
  const canCreateLocation = canCreate("work_locations_configuration");
  const canEditLocation = canEdit("work_locations_configuration");
  const canDeleteLocation = canDelete("work_locations_configuration");

  // Department Permissions
  const canViewDepartment = canView("department_configuration");
  const canCreateDepartment = canCreate("department_configuration");
  const canEditDepartment = canEdit("department_configuration");
  const canDeleteDepartment = canDelete("department_configuration");

  // Designation Permissions
  const canViewDesignation = canView("designation_configuration");
  const canCreateDesignation = canCreate("designation_configuration");
  const canEditDesignation = canEdit("designation_configuration");
  const canDeleteDesignation = canDelete("designation_configuration");

  const openModal = (formType, index = null, existing = null) => {
    setType(formType);
    setEditIndex(index);

    if (formType === "location") {
      setLocationForm(
        {
          name: existing?.name || "",
          state: existing?.state || "",
          city: existing?.city || "",
          pincode: existing?.pincode || "",
          address: existing?.address || "",
          latitude: existing?.workLocationGeoPoint?.coordinates?.[0] || "",
          longitude: existing?.workLocationGeoPoint?.coordinates?.[1] || "",
          radius: existing?.radius || 50,
        } || emptyLocation,
      );
      setRows([{ name: "" }]);
    } else {
      // dept / desig
      if (existing) {
        setRows([{ name: existing.name }]);
      } else {
        setRows([{ name: "" }]);
      }
      setLocationForm(emptyLocation);
    }
    setOpen(true);
  };

  const isLocationValid = () =>
    locationForm.name &&
    locationForm.state &&
    locationForm.city &&
    locationForm.pincode &&
    locationForm.address;

  const isRowsValid = () => rows.every((r) => r.name.trim() !== "");

  // --------------------------------------------
  // SAVE HANDLER
  // --------------------------------------------
  const saveData = async () => {
    try {
      if (type === "location" && !canCreateLocation && editIndex === null)
        return canCreate("work_locations_configuration", true);

      if (type === "location" && editIndex !== null && !canEditLocation)
        return canEdit("work_locations_configuration", true);

      if (type === "dept" && editIndex === null && !canCreateDepartment)
        return canCreate("department_configuration", true);

      if (type === "dept" && editIndex !== null && !canEditDepartment)
        return canEdit("department_configuration", true);

      if (type === "desig" && editIndex === null && !canCreateDesignation)
        return canCreate("designation_configuration", true);

      if (type === "desig" && editIndex !== null && !canEditDesignation)
        return canEdit("designation_configuration", true);

      if (type === "location") {
        if (!isLocationValid()) return toast.error("Fill all required fields.");
        const radiusNum = Number(locationForm.radius);

        if (locationForm.latitude && (Number.isNaN(radiusNum) || radiusNum < 50 || radiusNum > 1000)) {
          toast.error("Office radius must be between 50 and 1000 meters.");
          return;
        }
        if (editIndex !== null) {
          const existing = locations[editIndex];
          const res = await updateLocationService(existing._id, locationForm);
          const updated = [...locations];
          updated[editIndex] = res.location ?? res.data ?? locationForm;
          setLocations(updated);
          reloadOnboardingStatus();
          toast.success("Location updated");
        } else {
          const res = await createLocationService(locationForm);
          setLocations([...locations, res.location ?? res.data]);
          reloadOnboardingStatus();
          toast.success("Location added");
        }
      }

      if (type === "dept" || type === "desig") {
        if (!isRowsValid()) {
          toast.error(
            `${
              type === "dept" ? "Department" : "Designation"
            } names cannot be empty.`,
          );
          return;
        }

        // Extract trimmed lowercase names
        const newNames = rows.map((r) => r.name.trim().toLowerCase());

        // 1️⃣ Check duplicates inside modal itself
        const duplicatesInsideModal = newNames.filter(
          (name, idx) => newNames.indexOf(name) !== idx,
        );

        if (duplicatesInsideModal.length > 0) {
          toast.error("Duplicate names are not allowed inside the form.");
          return;
        }

        // 2️⃣ Check duplicates against existing database list
        const existingList =
          type === "dept"
            ? departments.map((d) => d.name.trim().toLowerCase())
            : designations.map((d) => d.name.trim().toLowerCase());

        const duplicatesWithExisting = newNames.some((name) =>
          existingList.includes(name),
        );

        if (duplicatesWithExisting && editIndex === null) {
          toast.error(
            `${type === "dept" ? "Department" : "Designation"} already exists.`,
          );
          return;
        }
      }

      if (type === "dept") {
        if (!isRowsValid())
          return toast.error("Department names cannot be empty.");

        const names = rows.map((r) => r.name.trim()).filter((v) => v !== "");

        if (names.length === 0)
          return toast.error("Please enter at least one department.");

        if (editIndex !== null) {
          const existing = departments[editIndex];
          const res = await updateDepartmentService(existing._id, {
            name: names[0],
          });
          const updated = [...departments];
          updated[editIndex] = res.department ?? res.data ?? { name: names[0] };
          setDepartments(updated);
          reloadOnboardingStatus();
          toast.success("Department updated");
        } else {
          const res = await createDepartmentService({ names });
          setDepartments([...departments, ...res.departments]);
          reloadOnboardingStatus();
          toast.success("Departments added");
        }
      }

      if (type === "desig") {
        if (!isRowsValid())
          return toast.error("Designation names cannot be empty.");

        const names = rows.map((r) => r.name.trim()).filter((v) => v !== "");

        if (names.length === 0)
          return toast.error("Please enter at least one designation.");

        if (editIndex !== null) {
          const existing = designations[editIndex];
          const res = await updateDesignationService(existing._id, {
            name: names[0],
          });
          const updated = [...designations];
          updated[editIndex] = res.designation ??
            res.data ?? { name: names[0] };
          setDesignations(updated);
          reloadOnboardingStatus();
          toast.success("Designation updated");
        } else {
          const res = await createDesignationService({ names });
          setDesignations([...designations, ...res.designations]);
          reloadOnboardingStatus();
          toast.success("Designations added");
        }
      }

      setOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save");
    }
  };

  // --------------------------------------------
  // DELETE HANDLER
  // --------------------------------------------
  const deleteItem = async (list, setter, index, type) => {
    try {
       if (type === "location" && !canDeleteLocation) {
         return canDelete("work_locations_configuration", true);
       }

       if (type === "dept" && !canDeleteDepartment) {
         return canDelete("department_configuration", true);
       }

       if (type === "desig" && !canDeleteDesignation) {
         return canDelete("designation_configuration", true);
       }

      const id = list[index]._id;

      if (type === "location") await deleteLocationService(id);
      if (type === "dept") await deleteDepartmentService(id);
      if (type === "desig") await deleteDesignationService(id);

      setter(list.filter((_, i) => i !== index));
      reloadOnboardingStatus();
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  // City list
  const stateIso = locationForm.state
    ? INDIA_STATES.find((s) => s.name === locationForm.state)?.isoCode
    : null;

  const cities = stateIso
    ? City.getCitiesOfState("IN", stateIso).map((c) => c.name)
    : [];

  // --------------------------------------------
  // RENDER TABLE
  // --------------------------------------------
  const renderTable = (title, data, cols, formType) => {
    const isWorkLocation = formType === "location";
    const isLocked =
      isWorkLocation && !addMultipleLocations && data.length >= 1;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            {title}
            {isLocked ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!isPaidOnline && isLocked}
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={() =>
                        navigate("/subscription", {
                          state: { reason: "feature_upgrade_required" },
                        })
                      }
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                      <Lock className="h-4 w-4 mr-1 text-muted-foreground" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Upgrade plan to add more locations
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button
                size="sm"
                disabled={
                  (formType === "location" && !canCreateLocation) ||
                  (formType === "dept" && !canCreateDepartment) ||
                  (formType === "desig" && !canCreateDesignation)
                }
                onClick={() => {
                  if (
                    formType === "location" &&
                    !canCreate("work_locations_configuration", true)
                  )
                    return;
                  if (
                    formType === "dept" &&
                    !canCreate("department_configuration", true)
                  )
                    return;
                  if (
                    formType === "desig" &&
                    !canCreate("designation_configuration", true)
                  )
                    return;
                  openModal(formType);
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent>
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  {cols.map((c) => (
                    <TableHead key={c}>{c}</TableHead>
                  ))}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={cols.length + 1}
                      className="text-center py-6 text-gray-500"
                    >
                      No records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((item, i) => (
                    <TableRow key={i}>
                      {cols.map((col) => (
                        <TableCell key={col}>
                          {item[col.toLowerCase()]}
                        </TableCell>
                      ))}

                      <TableCell className="text-right flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (
                              formType === "location" &&
                              !canEdit("work_locations_configuration", true)
                            )
                              return;
                            if (
                              formType === "dept" &&
                              !canEdit("department_configuration", true)
                            )
                              return;
                            if (
                              formType === "desig" &&
                              !canEdit("designation_configuration", true)
                            )
                              return;
                            openModal(formType, i, item);
                          }}
                          disabled={
                            (formType === "location" && !canEditLocation) ||
                            (formType === "dept" && !canEditDepartment) ||
                            (formType === "desig" && !canEditDesignation)
                          }
                        >
                          <Edit className="h-4 w-4 text-blue-600" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (
                              formType === "location" &&
                              !canDelete("work_locations_configuration", true)
                            )
                              return;
                            if (
                              formType === "dept" &&
                              !canDelete("department_configuration", true)
                            )
                              return;
                            if (
                              formType === "desig" &&
                              !canDelete("designation_configuration", true)
                            )
                              return;
                            openConfirm({
                              title: `Delete ${title.slice(0, -1)}`,
                              message: "Are you sure?",
                              onConfirm: () =>
                                deleteItem(
                                  data,
                                  formType === "location"
                                    ? setLocations
                                    : formType === "dept"
                                      ? setDepartments
                                      : setDesignations,
                                  i,
                                  formType,
                                ),
                            });
                          }}
                          disabled={
                            (formType === "location" && !canDeleteLocation) ||
                            (formType === "dept" && !canDeleteDepartment) ||
                            (formType === "desig" && !canDeleteDesignation)
                          }
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  };

  // --------------------------------------------
  // MODAL BODY
  // --------------------------------------------
  const renderRows = () => (
    <div className="space-y-3">
      {editIndex === null && type !== "location" && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Enter multiple names to add them in a batch.
        </p>
      )}

      {rows.map((r, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Input
            value={r.name}
            onChange={(e) => {
              const updated = [...rows];
              updated[i].name = e.target.value;
              setRows(updated);
            }}
            placeholder={`Enter ${
              type === "dept" ? "Department" : "Designation"
            } Name`}
            className="flex-1 dark:bg-gray-700 dark:border-gray-600"
          />

          {rows.length > 1 && editIndex === null && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setRows(rows.filter((_, idx) => idx !== i))}
              title="Remove row"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      ))}

      {editIndex === null && (
        <Button
          variant="outline"
          onClick={() => {
            if (rows.length > 0 && rows[rows.length - 1].name.trim() === "") {
              toast.error(
                "Please fill the current field before adding a new one.",
              );
              return;
            }
            setRows([...rows, { name: "" }]);
          }}
          className="mt-4 w-full justify-center border-dashed"
        >
          <Plus className="h-4 w-4 mr-2" /> Add More
        </Button>
      )}
    </div>
  );

  return (
    <>
      <div className="space-y-8">
        {canViewLocation && (
          <div id="setting-org-locations">
            {renderTable(
              "Work Locations",
              locations,
              ["Name", "State", "City", "Pincode"],
              "location",
            )}
          </div>
        )}

        {showDepartmentDesignation ? (
          <>
            <div id="setting-org-departments">
              {canViewDepartment &&
                renderTable("Departments", departments, ["Name"], "dept")}
            </div>

            <div id="setting-org-designations">
              {canViewDesignation &&
                renderTable("Designations", designations, ["Name"], "desig")}
            </div>
          </>
        ) : null}
      </div>

      {/* MODAL */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editIndex !== null ? "Edit" : "Add"}{" "}
              {type === "location"
                ? "Location"
                : type === "dept"
                  ? "Departments"
                  : "Designations"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {type === "location" ? (
              <>
                <InputField
                  label="Location Name"
                  value={locationForm.name}
                  onChange={(v) =>
                    setLocationForm({ ...locationForm, name: v })
                  }
                  placeholder="Eg. Head Office"
                />

                <div>
                  <Label htmlFor="state">State</Label>
                  <Select
                    value={locationForm.state}
                    onValueChange={(v) =>
                      setLocationForm({ ...locationForm, state: v, city: "" })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>

                    <SelectContent className="max-h-60">
                      {INDIA_STATES.map((s) => (
                        <SelectItem key={s.isoCode} value={s.name}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="city">City</Label>
                  <Select
                    value={locationForm.city}
                    onValueChange={(v) =>
                      setLocationForm({ ...locationForm, city: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select City" />
                    </SelectTrigger>

                    <SelectContent className="max-h-60">
                      {cities.map((city) => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <InputField
                  label="Pincode"
                  value={locationForm.pincode}
                  onChange={(v) =>
                    setLocationForm({ ...locationForm, pincode: v })
                  }
                  placeholder="Enter Pincode"
                />

                <InputField
                  label="Address"
                  value={locationForm.address}
                  onChange={(v) =>
                    setLocationForm({ ...locationForm, address: v })
                  }
                  placeholder="Enter Address"
                />

                {/* -------- Latitude / Longitude -------- */}
                <div className="grid grid-cols-2 gap-4">
                  <InputField
                    label="Latitude"
                    value={locationForm.latitude}
                    onChange={(v) =>
                      setLocationForm({ ...locationForm, latitude: v })
                    }
                    placeholder="Enter Latitude"
                  />

                  <InputField
                    label="Longitude"
                    value={locationForm.longitude}
                    onChange={(v) =>
                      setLocationForm({ ...locationForm, longitude: v })
                    }
                    placeholder="Enter Longitude"
                  />
                </div>

                <InputField
                  label="Office Radius (meters)"
                  value={locationForm.radius}
                  onChange={(v) =>
                    setLocationForm({
                      ...locationForm,
                      radius: v,
                    })
                  }
                  placeholder="e.g. 200"
                />

                <p className="text-xs text-muted-foreground mt-1">
                  Allowed range:{" "}
                  <span className="font-medium">50 – 1000 meters</span>
                </p>

                {/* -------- Map Picker Trigger -------- */}
                <div className="flex items-center justify-between rounded-md border bg-slate-50 dark:bg-slate-900 p-3">
                  <div className="text-sm">
                    <p className="font-semibold">Pinpoint on Map</p>
                    <p className="text-xs text-slate-500">
                      Recommended for accurate geofencing
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMapOpen(true)}
                  >
                    Open Map
                  </Button>
                </div>
              </>
            ) : (
              renderRows()
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveData}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Pinpoint Office Location</DialogTitle>
          </DialogHeader>

          <OfficeLocationPicker
            geoCoordinates={{
              lat: locationForm.latitude,
              lng: locationForm.longitude,
            }}
            radius={Number(locationForm.radius) || 100}
            onSave={({ latitude, longitude, radius }) => {
              setLocationForm({
                ...locationForm,
                latitude: latitude.toString(),
                longitude: longitude.toString(),
                radius: radius,
              });
              setMapOpen(false);
            }}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setMapOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={confirmData.title}
        message={confirmData.message}
        onConfirm={confirmData.onConfirm}
      />
    </>
  );
};

// -------------------------------------------------------
// SIMPLE INPUT FIELD COMPONENT
// -------------------------------------------------------
const InputField = ({
  label,
  value,
  onChange,
  isSelect = false,
  options = [],
  disabled = false,
  placeholder = "",
}) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    {isSelect ? (
      <select
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-2 border rounded-md dark:bg-gray-800"
      >
        <option value="">Select {label}</option>
        {options.map((op) => (
          <option key={op} value={op}>
            {op}
          </option>
        ))}
      </select>
    ) : (
      <Input
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    )}
  </div>
);

export default WorkLocationDeptDesignationTab;
